/**
 * 사내결재 문서 첨부 상자 — 지출결재 3종·회의록이 **같은 것을 쓴다** (v2.119.0).
 *
 * 원래 ExpenseDocModal 안에만 있던 UI 를 꺼냈다. 회의록에 똑같은 상자를 하나 더 그리면
 * 「한쪽만 고쳐지는」 자리가 생긴다 — 파일 형식·크기 표시·삭제 확인 문구가 화면마다
 * 달라지는 것이 그 시작이다.
 *
 * 두 가지 모드
 *   queue=true  (작성 화면) — 고른 파일을 **담아만 둔다**. 새 문서는 아직 문서번호가 없어
 *                             바로 올릴 수 없고, 작성을 취소하면 파일도 남지 않는다.
 *                             부모가 저장·제출한 뒤에 올린다.
 *   queue=false (보기 화면) — 이미 있는 문서라 고르는 즉시 올린다.
 *
 * ⚠ 서버는 **문서 작성자 본인이 결재 전(작성중·검토대기·반려)** 일 때만 올리고 지우게 한다.
 *   여기 canAdd/canDelete 는 그 규칙을 **화면에 비추는 것**일 뿐 통제가 아니다 —
 *   막는 것은 서버(routes/attachments)다.
 */

import * as api from '../lib/expense-api';
import { openPdfPreview } from '../lib/pdf-preview';

/** 목록에 그릴 첨부 한 건 (getExpenseDoc 의 receipts 한 줄) */
export interface AttachMeta {
  id: number;
  filename: string;
  mime_type: string;
  file_size: number;
}

interface Props {
  /** 상자 제목·버튼에 쓰는 이름 — '견적서' · '영수증·증빙' · '회의자료' */
  label: string;
  /** 이미 올라간 첨부 */
  uploaded: AttachMeta[];
  /** 아직 안 올라간 파일 (queue=false 면 빈 배열) */
  pending: File[];
  /** true = 담아 뒀다가 부모가 저장할 때 올린다 */
  queue: boolean;
  canAdd: boolean;
  canDelete: boolean;
  /** 무엇을 눌러야 올라가는가 — '제출할 때 올라감' · '저장할 때 올라감' */
  pendingWhen: string;
  /** 제목 옆 설명. 안 주면 지출결재 기본 문구 */
  hint?: string;
  /**
   * 아직 안 올린 파일이 있을 때 상자 아래에 뜨는 한 줄.
   * ⚠**화면마다 다르다.** 지출결재에는 [확인] 미리보기 단추가 있지만 회의록에는 없다 —
   *   없는 단추를 안내하면 그것을 찾다 헤맨다(2026-09-03 화면 확인에서 실제로 걸렸다).
   */
  pendingNote?: string;
  onPick: (files: File[]) => void;
  onRemovePending: (idx: number) => void;
  onDelete: (a: AttachMeta) => void;
}

export function ExpenseAttachBox({
  label, uploaded, pending, queue, canAdd, canDelete,
  pendingWhen, hint, pendingNote, onPick, onRemovePending, onDelete,
}: Props) {
  const count = uploaded.length + (queue ? pending.length : 0);
  return (
    <div className="border border-slate-200 rounded p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
        <div className="text-xs font-semibold text-slate-500">
          {label} 첨부 ({count}건)
          <span className="font-normal text-slate-400 ml-2">
            {hint ?? '제출한 PDF 뒤에 참고자료로 붙습니다 · PDF · 이미지 · 엑셀'}
          </span>
        </div>
        {canAdd && (
          <label className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50 cursor-pointer">
            + {label} 첨부
            <input type="file" className="hidden" multiple accept={api.ATTACH_ACCEPT}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length > 0) onPick(files);
                e.target.value = '';
              }} />
          </label>
        )}
      </div>
      {count === 0 ? (
        <div className="text-xs text-slate-500">첨부 없음</div>
      ) : (
        <ul className="space-y-1">
          {uploaded.map((r) => (
            <li key={r.id} className="flex items-center gap-2 text-xs">
              <button type="button" className="text-blue-600 hover:underline truncate"
                onClick={() => openPdfPreview({
                  url: `${api.attachmentDownloadUrl(r.id)}?inline=1`,
                  downloadUrl: api.attachmentDownloadUrl(r.id),
                  filename: r.filename,
                  mime: r.mime_type,
                })}>
                {r.filename}
              </button>
              <span className="text-slate-400">{Math.round(r.file_size / 1024)}KB</span>
              {canDelete && (
                <button type="button" className="text-slate-300 hover:text-rose-500" title="삭제"
                  onClick={() => {
                    if (confirm(`첨부 "${r.filename}" 을 삭제할까요?`)) onDelete(r);
                  }}>×</button>
              )}
            </li>
          ))}
          {queue && pending.map((f, i) => (
            <li key={`p${i}-${f.name}`} className="flex items-center gap-2 text-xs">
              <span className="truncate text-slate-700">{f.name}</span>
              <span className="text-slate-400">{Math.round(f.size / 1024)}KB</span>
              <span className="text-xs text-amber-600">{pendingWhen}</span>
              <button type="button" className="text-slate-300 hover:text-rose-500" title="목록에서 빼기"
                onClick={() => onRemovePending(i)}>×</button>
            </li>
          ))}
        </ul>
      )}
      {/* 아직 안 올라간 파일이 어떻게 되는지 — 없는 걸 찾다 헤매지 않게 미리 적는다 */}
      {queue && pending.length > 0 && (
        <div className="text-xs text-slate-500 mt-1.5">
          {pendingNote
            ?? `방금 고른 파일은 ${pendingWhen.replace('할 때 올라감', '')} 후에 붙습니다 — [확인] 미리보기에는 아직 안 보입니다.`}
        </div>
      )}
    </div>
  );
}
